import type { WidgetLanguage } from '../types';

/**
 * Widget UI translations (RDPNEXUS-844). English-keyed: keys are the English source
 * strings (so existing labels double as lookup keys and English needs no entry).
 * Copied verbatim from the legacy widget's source of truth:
 *   ~/Workspace/plumprint-image-service/widget/coffee/lib/stringLibrary.coffee
 *
 * Covers the metric category titles, the metric sub-type labels rendered in the
 * stat rows (e.g. "Citation Indexes", "Readers"), the "see details" CTA, and the
 * empty state. Labels not present here (e.g. newer API strings) fall back to English
 * via the translator (see i18n.tsx). API source names and brand names that have no
 * entry pass through unchanged.
 */
export const TRANSLATIONS: Record<Exclude<WidgetLanguage, 'en'>, Record<string, string>> = {
  es: {
    'Abstract Views': 'Vistas de Resumen',
    Abstract: 'Resumen',
    'Affiliated Researchers': 'Investigadores Afiliados',
    Article: 'Artículo',
    'Artifact Summary': 'Resumen Productos de Investigación',
    Audio: 'Audio',
    Bibliography: 'Bibliography',
    'Blog Mentions': 'Menciones Blog',
    'Blog Post': 'Comentarios de Blog',
    Blog: 'Blog',
    Blogs: 'Blogs',
    'Book Chapter': 'Capítulo de Libro',
    Book: 'Libro',
    Bookmarks: 'Marcadores',
    Brief: 'Compendio',
    Captures: 'Capturas',
    Case: 'Caso',
    Catalogue: 'Catálogo',
    'Citation Indexes': 'Índice de Citas',
    Citations: 'Citas',
    Clicks: 'Clics',
    'Clinical Citations': 'Citas Clínicas',
    'Clinical Trial': 'Ensayo Clínico',
    Code: 'Código',
    Collaborators: 'Colaboradores',
    Collection: 'Coleción',
    Comments: 'Comentarios',
    'Conference Paper': 'Memorias de Congresos',
    Correction: 'Corrección',
    'Data Set': 'Conjunto de Datos',
    'Data Views': 'Vistas Apoyadas',
    'Design/Architecture': 'Diseño / Arquitectura',
    Downloads: 'Descargas',
    'Economic Blog Mentions': 'Menciones Blogs Económicos',
    'Economics Blog Mentions': 'Menciones Blogs Económicos',
    'Economics Blogs': 'Menciones Blogs Económicos',
    'Exhibition/Event': 'Exhibición/Evento',
    'Exhibition / Event': 'Exhibición/Evento',
    'Expert Opinion': 'Opinión de Experto',
    'Exports-Saves': 'Exportados / Guardados',
    Favorites: 'Favoritos',
    Figure: 'Cifra',
    'File Set': 'Conjunto de Archivos',
    Followers: 'Seguidores',
    Forks: 'Conexiones',
    'Forum Topic Count': 'Conteo Temas Foro',
    'Full Text Views': 'Vistas Txt Completo',
    Gists: 'Conteo Esencial',
    'Government Document': 'Documento de Gobierno',
    Grant: 'Beca',
    Guideline: 'Guía',
    'HTML Views': 'Vistas HTML',
    Holdings: 'Acervo',
    Image: 'Imagen',
    Interview: 'Entrevista',
    Issue: 'Número',
    Journal: 'Publicación',
    Letter: 'Ensayo',
    Likes: 'Me Gusta',
    'Link-outs': 'Vínculos',
    Links: 'Enlaces',
    'Live Performance': 'Presentación en Vivo',
    Manuscript: 'Manuscrito',
    Map: 'Mapa',
    Media: 'Medios',
    Mentions: 'Menciones',
    'Music Score': 'Partitura Musical',
    'News Mentions': 'Menciones Noticias',
    News: 'Noticias',
    Newsletter: 'Boletín Informativo',
    'No metrics available.': 'No hay métricas disponibles.',
    'Online Course': 'Curso en línea',
    Other: 'Otro',
    'PDF Views': 'Vistas PDF',
    Paper: 'Documento de Trabajo',
    'Patent Citations': 'Citas de Patentes',
    Patent: 'Patentes',
    Plays: 'Reproducciones',
    Policy: 'Políticas',
    'Policy Citations': 'Citas de Políticas',
    Poster: 'Afiche',
    Preprint: 'Preimpresión',
    PrePrint: 'Preimpresión',
    Presentation: 'Presentación',
    'Press Release': 'Comunicado de Prensa',
    Project: 'Proyecto',
    Ratings: 'Clasificación',
    Readers: 'Lectores',
    'Recent Artifacts': 'Piezas de Investigación Recientes',
    Recommendations: 'Recomendaciones',
    'Recorded Work': 'Grabación',
    'Reference Entry': 'Referencia',
    References: 'Referencias',
    Report: 'Informe',
    Researchers: 'Investigadores',
    Retraction: 'Retracción',
    Reviews: 'Críticas',
    Review: 'Críticas',
    'Sample Downloads': 'Descarga del Ejemplo',
    Scores: 'Calificación',
    Score: 'Calificación',
    Shares: 'Compartir',
    'Shares, Likes & Comments': 'Compartidos, Me Gusta y Comentarios',
    'Social Media': 'Redes Sociales',
    Speech: 'Discurso',
    Standard: 'Estándar',
    Subscribers: 'Subscriptores',
    'Supporting Data Views': 'Observaciones de Datos',
    Syllabus: 'Plan de Estudios',
    'Textual Work': 'Escrito',
    'Thesis / Dissertation': 'Tesis / Disertación',
    Tweets: 'Tuits',
    Unknown: 'Desconocido',
    Usage: 'Uso',
    Video: 'Video',
    Views: 'Vistas',
    'Visual Arts': 'Artes Visuales',
    Volume: 'Volumen',
    Watchers: 'Observadores',
    'Web Page': 'Página Web',
    'Web Resource': 'Recurso Web',
    'see details': 'Ver detalles',
    'Brochure / Pamphlet': 'Folleto / Panfleto',
    Commentary: 'Comentario',
    Editorial: 'Editorial',
    'Research Proposal': 'Propuesta de Investigación',
    'Technical Documentation': 'Documento Técnico',
    'Code / Software': 'Código / Software',
    'Lecture / Presentation': 'Presentación',
    'Learning Object': 'objetos de aprendizaje',
    'Research Artifact': 'Pieza Investigativa',
    'PlumX Metrics Detail Page': 'Página de detalle de PlumX Metrics',
    Emails: 'Correos electrónicos',
    'ePub Downloads': 'Descargas en ePub',
    'Exports/Saves': 'Exportaciones/Guardados',
    Exports: 'Exportaciones',
    Saves: 'Guardados',
    'Figure Views': 'Visualizaciones de imágenes',
    'Forum Topics': 'Temas de foro',
    Groups: 'Grupos',
    'PDF Downloads': 'Descargas en PDF',
    'Print-outs': 'Copias impresas',
    Reference: 'Referencia',
    'Unique IP Views': 'Visualizaciones IP únicas',
    'XML Views': 'Visualizaciones XML',
    'Artifact Type': 'Tipo de artefacto',
  },
  ru: {
    Abstract: 'Аннотация',
    'Abstract Views': 'Просмотры аннотации',
    'Affiliated Researchers': 'Аффилиированные исследователи',
    Article: 'Статья',
    'Artifact Summary': 'Краткая характеристика артефакта',
    Audio: 'Аудио-материал',
    Bibliography: 'Библиография',
    Blog: 'Блог',
    Blogs: 'Блог',
    'Blog Mentions': 'Упоминания в блогах',
    'Blog Post': 'Пост в блоге',
    Book: 'Книга',
    'Book Chapter': 'Глава книги',
    'Book Series': 'Серия книг',
    'Book Set': 'Набор книг',
    Bookmarks: 'Закладки',
    Brief: 'Кратко',
    'Brochure / Pamphlet': 'Проспект / брошюра',
    Captures: 'Получения',
    Case: 'Отдельный случай',
    Catalogue: 'Каталог',
    'Citation Indexes': 'Указатели цитирований',
    Citations: 'Цитирования',
    Clicks: 'Нажатия',
    'Clinical Citations': 'Цитирования в клинических документах',
    'Clinical Trial': 'Клиническое исследование',
    Code: 'Код',
    'Code / Software': 'Программный код',
    Collaborators: 'Коллеги',
    Collection: 'Подборка',
    Commentary: 'Комментарий',
    Comments: 'Комментарии',
    'Conference Paper': 'Научный доклад',
    'Conference Proceeding': 'Материал конференции',
    Correction: 'Исправление',
    'Data Set': 'Набор данных',
    'Data Views': 'Просмотры данных',
    'Design/Architecture': 'Дизайн / архитектура',
    Downloads: 'Загрузки',
    'Economic Blog Mentions': 'Упоминания в экономических блогах',
    'Economics Blog Mentions': 'Упоминания в экономических блогах',
    'Economics Blogs': 'Упоминания в экономических блогах',
    Editorial: 'Редакторская статья',
    'Exhibition/Event': 'Событие / выставка',
    'Exhibition / Event': 'Событие / выставка',
    'Expert Opinion': 'Мнение экспертов',
    'Exports-Saves': 'Экспорты / сохранения',
    Favorites: 'Избранное',
    Figure: 'Рисунок',
    'File Set': 'Набор файлов',
    Followers: 'Подписчики',
    Forks: 'Копии репозитория',
    'Forum Topic Count': 'Счетчик тем форума',
    'Full Text Views': 'Просмотры полного текста',
    Gists: 'Gists',
    'Government Document': 'Правительственный документ',
    Grant: 'Грант',
    Guideline: 'Руководство',
    Holdings: 'В библиотеках',
    'HTML Views': 'Просмотры HTML',
    Image: 'Изображение',
    Interview: 'Интервью',
    Issue: 'Номер',
    Journal: 'Журнал',
    'Learning Object': 'Объект обучения',
    'Lecture / Presentation': 'Лекция / презентация',
    Letter: 'Письмо',
    Likes: 'Нравится',
    'Link-outs': 'Ссылки на материал',
    Links: 'Ссылки',
    'Live Performance': 'Живое выступление',
    Manuscript: 'Рукопись',
    Map: 'Карта',
    Media: 'Видео-, аудио',
    Mentions: 'Упоминания',
    'Music Score': 'Музыкальное сопровождение',
    News: 'Новости',
    'News Mentions': 'Упоминания в новостях',
    Newsletter: 'Информационное письмо',
    'No metrics available.': 'Нет доступных показателей.',
    'Online Course': 'Курс обучения онлайн',
    Other: 'Другое',
    Paper: 'Статья',
    Patent: 'Патент',
    'Patent Citations': 'Цитирования в патентах',
    'PDF Views': 'Просмотры PDF',
    Plays: 'Просмотры медиа',
    Policy: 'Полис',
    'Policy Citations': 'Цитирования в полисах',
    Poster: 'Постер',
    Preprint: 'Препринт',
    PrePrint: 'Препринт',
    Presentation: 'Презентация',
    'Press Release': 'Пресс-релиз',
    Project: 'Проект',
    'Q&A Site Mentions': 'Вопросы и ответы — упоминания на сайте',
    Ratings: 'Рейтинг',
    Readers: 'Читатели',
    'Recent Artifacts': 'Недавние артефакты',
    Recommendations: '«Рекомендую»',
    'Recorded Work': 'Записанные работы',
    'Reference Entry': 'Ссылка',
    'Reference Series': 'Серия ссылок',
    'Reference Set': 'Набор ссылок',
    References: 'Пристатейные ссылки',
    Report: 'Отчет',
    'Research Artifact': 'Исследовать артефакт',
    'Research Proposal': 'План исследования',
    Researchers: 'Исследователи',
    Retraction: 'Опровержение',
    Reviews: 'Редакции',
    Review: 'Редакции',
    'Sample Downloads': 'Загрузки образцов',
    Scores: 'Баллы',
    Score: 'Баллы',
    'see details': 'см. подробнее',
    Shares: 'Поделились',
    'Shares, Likes & Comments': 'Поделились, «нравится» и комментарии',
    'Social Media': 'Социальные сети',
    Speech: 'Выступление',
    Standard: 'Стандарт',
    Subscribers: 'Пользователи',
    'Supporting Data Views': 'Просмотры обосновывающих данных',
    Syllabus: 'Программа обучения',
    'Technical Documentation': 'Техническая документация',
    'Textual Work': 'Реферат',
    'Thesis / Dissertation': 'Тезисы / диссертационная работа',
    Tweets: 'Твиты',
    Unknown: 'Неизвестно',
    Usage: 'Степень использования',
    Video: 'Видео-материал',
    Views: 'Просмотры',
    'Visual Arts': 'Изобразительное искусство',
    Volume: 'Том',
    Watchers: 'Смотрители',
    'Web Page': 'Веб-страница',
    'Web Resource': 'Веб-ресурс',
    'PlumX Metrics Detail Page': 'Страница подробных показателей PlumX',
    'Artifact Type': 'Тип артефакта',
    Emails: 'Эл. письма',
    'ePub Downloads': 'Скачивания ePub',
    'Exports/Saves': 'Экспорты / сохранения',
    Exports: 'Экспорты',
    Saves: 'Сохранения',
    'Figure Views': 'Просмотры рисунков',
    'Forum Topics': 'Темы форумов',
    Groups: 'Группы',
    'PDF Downloads': 'Скачивания PDF',
    'Print-outs': 'Распечатывания',
    Reference: 'Источник',
    'Unique IP Views': 'Просмотры с уникального IP-адреса',
    'XML Views': 'Просмотры XML',
  },
  jp: {
    Abstract: '抄録',
    'Abstract Views': '抄録表示',
    'Affiliated Researchers': '所属研究者',
    Article: '原著論文',
    'Artifact Summary': 'アーティファクトの要旨',
    Audio: 'オーディオ',
    Bibliography: '文献目録',
    Blog: 'ブログ',
    Blogs: 'ブログ',
    'Blog Mentions': 'ブログの言及',
    'Blog Post': 'ブログ記事',
    Book: '書籍',
    'Book Chapter': '書籍の章',
    'Book Series': '書籍シリーズ',
    'Book Set': '書籍セット',
    Bookmarks: 'ブックマーク',
    Brief: '要約',
    'Brochure / Pamphlet': 'パンフレット',
    Captures: 'キャプチャ',
    Case: '事例',
    Catalogue: 'カタログ',
    'Citation Indexes': '被引用数',
    Citations: '被引用数',
    Clicks: 'クリック',
    'Clinical Citations': '臨床被引用数',
    'Clinical Trial': '臨床試験',
    Code: 'コード',
    'Code / Software': 'コード/ソフトウエア',
    Collaborators: '共同研究者',
    Collection: 'コレクション',
    Commentary: '論評解説',
    Comments: 'コメント',
    'Conference Paper': '会議論文',
    'Conference Proceeding': '会議議事録',
    Correction: '訂正',
    'Data Set': 'データセット',
    'Data Views': 'データ表示',
    'Design/Architecture': 'デザイン/建築',
    Downloads: 'ダウンロード',
    'Economic Blog Mentions': '経済関連ブログの言及',
    'Economics Blog Mentions': '経済関連ブログの言及',
    'Economics Blogs': '経済関連ブログの言及',
    Editorial: '論説',
    'Exhibition/Event': '展示/イベント',
    'Exhibition / Event': '展示/イベント',
    'Expert Opinion': '専門家の意見',
    'Exports-Saves': 'エクスポート/保存',
    Favorites: 'お気に入り',
    Figure: '図',
    'File Set': 'ファイルセット',
    Followers: 'フォロアー',
    Forks: 'フォーク',
    'Forum Topic Count': 'フォーラムトピック数',
    'Full Text Views': 'フルテキスト表示',
    Gists: 'ギスト',
    'Government Document': '公文書',
    Grant: '助成',
    Guideline: 'ガイドライン',
    Holdings: '所蔵',
    'HTML Views': 'HTML表示',
    Image: '画像',
    Interview: 'インタビュー',
    Issue: '号',
    Journal: 'ジャーナル',
    'Learning Object': 'ラーニングオブジェクト',
    'Lecture / Presentation': '講義/プレゼンテーション',
    Letter: '編集者への手紙',
    Likes: 'いいね',
    'Link-outs': 'リンクアウト',
    Links: 'リンク',
    'Live Performance': '実演',
    Manuscript: '原稿',
    Map: '地図',
    Media: 'メディア',
    Mentions: '言及',
    'Music Score': '譜面',
    News: 'ニュース',
    'News Mentions': 'ニュースの言及',
    Newsletter: 'ニュースレター',
    'No metrics available.': '該当する指標がありません。',
    'Online Course': 'オンライン講座',
    Other: 'その他',
    Paper: '書類',
    Patent: '特許',
    'Patent Citations': '特許被引用数',
    'PDF Views': 'PDF表示',
    Plays: '再生',
    Policy: 'ポリシー',
    'Policy Citations': 'ポリシー被引用数',
    Poster: 'ポスター',
    Preprint: 'プレプリント',
    PrePrint: 'プレプリント',
    Presentation: 'プレゼンテーション',
    'Press Release': 'プレスリリース',
    Project: 'プロジェクト',
    'Q&A Site Mentions': 'Q&A サイトの記事の言及',
    Ratings: '評価',
    Readers: '読者',
    'Recent Artifacts': '近代のアーティファクト',
    Recommendations: '推薦',
    'Recorded Work': '研究記録',
    'Reference Entry': '参考事項',
    'Reference Series': '参考文献シリーズ',
    'Reference Set': '参考文献セット',
    References: '参考文献',
    Report: 'レポート',
    'Research Artifact': '研究対象アーティファクト',
    'Research Proposal': '研究企画',
    Researchers: '研究者',
    Retraction: '取消し',
    Reviews: 'レビュー論文',
    Review: 'レビュー論文',
    'Sample Downloads': 'サンプルのダウンロード',
    Scores: 'スコア',
    Score: 'スコア',
    'see details': '詳細を参照',
    Shares: '共有',
    'Shares, Likes & Comments': 'シェア、いいね、 コメント',
    'Social Media': 'ソーシャルメディア',
    Speech: 'スピーチ',
    Standard: '基準',
    Subscribers: '購読者',
    'Supporting Data Views': '添付資料表示',
    Syllabus: 'シラバス',
    'Technical Documentation': '技術文書',
    'Textual Work': '原文書類',
    'Thesis / Dissertation': '学位論文',
    Tweets: 'ツイート',
    Unknown: '不明',
    Usage: '利用',
    Video: '映像',
    Views: '表示',
    'Visual Arts': '視覚芸術',
    Volume: '巻',
    Watchers: 'ウォッチャー',
    'Web Page': 'ウェブページ',
    'Web Resource': 'ウェブ資源',
    'PlumX Metrics Detail Page': 'PlumX指標詳細ページ',
    'Artifact Type': 'アーティファクトタイプ',
    Emails: 'E-mail',
    'ePub Downloads': 'ePubダウンロード',
    'Exports/Saves': 'エキスポート/保存',
    Exports: 'エキスポート',
    Saves: '保存',
    'Figure Views': '図表示',
    'Forum Topics': 'フォーラムトピック',
    Groups: 'グループ',
    'PDF Downloads': 'PDFダウンロード',
    'Print-outs': 'プリントアウト',
    Reference: '参考文献',
    'Unique IP Views': '固有IP表示',
    'XML Views': 'XML表示',
  },
  cn: {
    Abstract: '摘要',
    'Abstract Views': '摘要浏览次数',
    'Affiliated Researchers': '附属研究人员',
    Article: '文章',
    'Artifact Summary': '人工制品概述',
    Audio: '音频',
    Bibliography: '参考书目',
    Blog: '博客',
    Blogs: '博客',
    'Blog Mentions': '博客提述',
    'Blog Post': '博客文章',
    Book: '书籍',
    'Book Chapter': '书籍章节',
    'Book Series': '书籍系列',
    'Book Set': '书籍集',
    Bookmarks: '书签',
    Brief: '概要',
    'Brochure / Pamphlet': '宣传册/小册子',
    Captures: '捕获',
    Case: '案例',
    Catalogue: '目录',
    'Citation Indexes': '引用索引',
    Citations: '引文',
    Clicks: '点击量',
    'Clinical Citations': '临床引用',
    'Clinical Trial': '临床试验',
    Code: '代码',
    'Code / Software': '代码/软件',
    Collaborators: '合作者',
    Collection: '集合',
    Commentary: '注释',
    Comments: '评论',
    'Conference Paper': '会议文件',
    'Conference Proceeding': '会议纪要',
    Correction: '修正',
    'Data Set': '数据集',
    'Data Views': '数据视图',
    'Design/Architecture': '设计/架构',
    Downloads: '下载量',
    'Economic Blog Mentions': '经济博客提述',
    'Economics Blog Mentions': '经济博客提述',
    'Economics Blogs': '经济博客提述',
    Editorial: '社论',
    'Exhibition/Event': '展览/事件',
    'Exhibition / Event': '展览/事件',
    'Expert Opinion': '专家意见',
    'Exports-Saves': '导出/保存',
    Favorites: '收藏',
    Figure: '图形',
    'File Set': '文件集',
    Followers: '粉丝',
    Forks: '复制库',
    'Forum Topic Count': '论坛话题计数',
    'Full Text Views': '全文浏览次数',
    Gists: 'Gist',
    'Government Document': '政府文件',
    Grant: '拨款',
    Guideline: '指南',
    Holdings: '保存数',
    'HTML Views': 'HTML 浏览次数',
    Image: '图像',
    Interview: '访谈',
    Issue: '期',
    Journal: '期刊',
    'Learning Object': '学习对象',
    'Lecture / Presentation': '讲座/展示',
    Letter: '信件',
    Likes: '点赞',
    'Link-outs': '外部链接',
    Links: '链接',
    'Live Performance': '实况演出',
    Manuscript: '手稿',
    Map: '地图',
    Media: '媒体',
    Mentions: '提述',
    'Music Score': '乐谱',
    News: '新闻',
    'News Mentions': '新闻提述',
    Newsletter: '简讯',
    'No metrics available.': '没有可用的指标。',
    'Online Course': '在线课程',
    Other: '其他',
    Paper: '论文',
    Patent: '专利',
    'Patent Citations': '专利引用',
    'PDF Views': 'PDF 浏览次数',
    Plays: '播放次数',
    Policy: '政策',
    'Policy Citations': '政策引用',
    Poster: '海报',
    Preprint: '预印件',
    PrePrint: '预印件',
    Presentation: '展示',
    'Press Release': '新闻稿',
    Project: '项目',
    'Q&A Site Mentions': '问答网站提及',
    Ratings: '评分',
    Readers: '读者',
    'Recent Artifacts': '最近的人工制品',
    Recommendations: '推荐',
    'Recorded Work': '记录的工作',
    'Reference Entry': '参考条目',
    'Reference Series': '参考文献系列',
    'Reference Set': '参考文献集',
    References: '参考文献',
    Report: '报告',
    'Research Artifact': '研究人工制品',
    'Research Proposal': '研究计划',
    Researchers: '研究人员',
    Retraction: '撤销',
    Review: '评论',
    Reviews: '评论',
    'Sample Downloads': '样本下载次数',
    Scores: '得分',
    Score: '得分',
    'see details': '查看详情',
    Shares: '分享',
    'Shares, Likes & Comments': '分享、点赞及 评论',
    'Social Media': '社交媒体',
    Speech: '演讲',
    Standard: '标准',
    Subscribers: '注册用户',
    'Supporting Data Views': '支持数据浏览次数',
    Syllabus: '教学大纲',
    'Technical Documentation': '技术文档',
    'Textual Work': '文字作品',
    'Thesis / Dissertation': '论文/学位论文',
    Tweets: '推文',
    Unknown: '未知',
    Usage: '使用',
    Video: '视频',
    Views: '浏览次数',
    'Visual Arts': '视觉艺术',
    Volume: '卷',
    Watchers: '观察者',
    'Web Page': '网页',
    'Web Resource': '网络资源',
    'PlumX Metrics Detail Page': 'PlumX 指标详细信息页面',
    'Artifact Type': '人工制品类型',
    Emails: '电子邮件',
    'ePub Downloads': 'ePub 下载',
    'Exports/Saves': '导出/保存',
    Exports: '导出',
    Saves: '保存',
    'Figure Views': '图片视图',
    'Forum Topics': '论坛主题',
    Groups: '分组',
    'PDF Downloads': 'PDF 下载',
    'Print-outs': '打印输出',
    Reference: '参考文献',
    'Unique IP Views': '唯一 IP 视图',
    'XML Views': 'XML 视图',
  },
  tw: {
    Abstract: '摘要',
    'Abstract Views': '摘要檢視',
    'Affiliated Researchers': '工藝品研究人員',
    Article: '文章',
    'Artifact Summary': '工藝品摘要',
    Audio: '音訊',
    Bibliography: '參考書目',
    Blog: '部落格',
    Blogs: '部落格',
    'Blog Mentions': '部落格提及次數',
    'Blog Post': '部落格貼文',
    Book: '書籍',
    'Book Chapter': '書籍章節',
    'Book Series': '系列叢書',
    'Book Set': '套書',
    Bookmarks: '書籤',
    Brief: '簡介',
    'Brochure / Pamphlet': '手冊/小手冊',
    Captures: '擷取次數',
    Case: '案例',
    Catalogue: '目錄',
    'Citation Indexes': '引用索引',
    Citations: '引用情況',
    Clicks: '點擊次數',
    'Clinical Citations': '臨床引用次數',
    'Clinical Trial': '臨床試驗',
    Code: '代碼',
    'Code / Software': '程式碼/軟體',
    Collaborators: '合作者',
    Collection: '集合',
    Commentary: '評論',
    Comments: '評論',
    'Conference Paper': '研討會論文',
    'Conference Proceeding': '會議論文集',
    Correction: '修正',
    'Data Set': '資料集',
    'Data Views': '資料檢視',
    'Design/Architecture': '設計/架構',
    Downloads: '下載次數',
    'Economic Blog Mentions': '經濟部落格提及次數',
    'Economics Blog Mentions': '經濟部落格提及次數',
    'Economics Blogs': '經濟部落格提及次數',
    Editorial: '編輯',
    'Exhibition/Event': '展覽/活動',
    'Exhibition / Event': '展覽/活動',
    'Expert Opinion': '專家意見',
    'Exports-Saves': '匯出/儲存',
    Favorites: '我的最愛',
    Figure: '數字',
    'File Set': '檔案集合',
    Followers: '追蹤者',
    Forks: 'Fork',
    'Forum Topic Count': '論壇主題計數',
    'Full Text Views': '全文檢視',
    Gists: 'Gists',
    'Government Document': '政府文件',
    Grant: '補助金',
    Guideline: '指南',
    Holdings: '包含內容',
    'HTML Views': 'HTML 檢視',
    Image: '圖像',
    Interview: '訪談',
    Issue: '問題',
    Journal: '期刊',
    'Learning Object': '學習目標',
    'Lecture / Presentation': '演講/簡報',
    Letter: '信函',
    Likes: '讚',
    'Link-outs': '對外連結',
    Links: '連結',
    'Live Performance': '現場表現',
    Manuscript: '手稿',
    Map: '地圖',
    Media: '媒體',
    Mentions: '提及次數',
    'Music Score': '樂譜',
    News: '新聞',
    'News Mentions': '新聞提及次數',
    Newsletter: '新聞簡訊',
    'No metrics available.': '沒有可用的測量指標。',
    'Online Course': '線上課程',
    Other: '其他',
    Paper: '書面',
    Patent: '專利',
    'Patent Citations': '專利引用次數',
    'PDF Views': 'PDF 檢視',
    Plays: '播放次數',
    Policy: '政策',
    'Policy Citations': '政策引用次數',
    Poster: '海報',
    Preprint: '預印本',
    PrePrint: '預印本',
    Presentation: '簡報',
    'Press Release': '新聞稿',
    Project: '專案',
    'Q&A Site Mentions': '問答網站引用',
    Ratings: '評級',
    Readers: '讀者',
    'Recent Artifacts': '最近的工藝品',
    Recommendations: '推薦',
    'Recorded Work': '記錄的工作',
    'Reference Entry': '參考項目',
    'Reference Series': '參考系列',
    'Reference Set': '參考集',
    References: '參考文獻',
    Report: '報告',
    'Research Artifact': '研究工藝品',
    'Research Proposal': '研究提案',
    Researchers: '研究人員',
    Retraction: '撤回',
    Reviews: '評論',
    Review: '評論',
    'Sample Downloads': '範例下載',
    Scores: '分數',
    Score: '分數',
    'see details': '請參閱詳細資料',
    Shares: '分享',
    'Shares, Likes & Comments': '分享、讚 & 評論',
    'Social Media': '社交媒體',
    Speech: '演講',
    Standard: '標準',
    Subscribers: '訂閱者',
    'Supporting Data Views': '佐證資料檢視',
    Syllabus: '大綱',
    'Technical Documentation': '技術文件',
    'Textual Work': '文字工作',
    'Thesis / Dissertation': '命題/論文',
    Tweets: '推文',
    Unknown: '未知的',
    Usage: '使用量',
    Video: '視訊',
    Views: '檢視',
    'Visual Arts': '視覺藝術',
    Volume: '音量',
    Watchers: '觀察者',
    'Web Page': '網頁',
    'Web Resource': '網頁資源',
    'PlumX Metrics Detail Page': 'PlumX 測量指標詳細資料頁面',
    'Artifact Type': '手工藝品類型',
    Emails: '電子郵箱',
    'ePub Downloads': 'ePub 下載',
    'Exports/Saves': '匯出/儲存',
    Exports: '匯出',
    Saves: '儲存',
    'Figure Views': '圖案檢視',
    'Forum Topics': '論壇主題',
    Groups: '群組',
    'PDF Downloads': 'PDF 下載',
    'Print-outs': '列印',
    Reference: '參考文獻',
    'Unique IP Views': '特有的 IP 檢視',
    'XML Views': 'XML 檢視',
  },
};
